import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { MaskedLine, FadeUp, Kicker } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SERVICES = [
  "Progettazione giardino",
  "Manutenzione ordinaria",
  "Potatura e cura alberi",
  "Impianti di irrigazione",
  "Terrazzi e balconi",
  "Altro",
];

const INFO = [
  { icon: Phone, label: "Telefono", value: "+39 070 000 0000" },
  { icon: Mail, label: "Email", value: "info@lucamannugiardini.it" },
  { icon: MapPin, label: "Zona", value: "Cagliari e tutta la Sardegna" },
  { icon: Clock, label: "Orari", value: "Lun – Sab, 8:00 – 18:00" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: SERVICES[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { document.title = "Contatti e Preventivi — Luca Mannu Giardini"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setSent(true);
      toast.success("Richiesta inviata. Ti risponderò entro 48 ore.");
    } catch (err) {
      toast.error(apiError(err, "Invio non riuscito. Riprova."));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full border-line bg-sand/70 text-forest placeholder:text-sage/60 focus-visible:ring-terra";

  return (
    <main className="pt-32" data-testid="contact-page">
      <header className="mx-auto max-w-7xl px-6 lg:px-10">
        <Kicker>Contatti & Preventivi</Kicker>
        <h1 className="mt-4 font-serif text-5xl tracking-tight text-forest md:text-6xl">
          <MaskedLine delay={0.1}>Raccontami</MaskedLine>
          <MaskedLine delay={0.22} className="italic text-moss">il tuo spazio verde.</MaskedLine>
        </h1>
        <FadeUp delay={0.35}>
          <p className="mt-6 max-w-xl leading-relaxed text-sage">
            Compila la scheda: ti ricontatterò entro 48 ore per fissare un sopralluogo gratuito e preparare un preventivo su misura.
          </p>
        </FadeUp>
      </header>

      <section className="mx-auto grid max-w-7xl gap-16 px-6 pb-32 pt-16 lg:grid-cols-12 lg:px-10">
        <FadeUp className="lg:col-span-7">
          {sent ? (
            <div className="flex h-full min-h-96 flex-col items-start justify-center rounded-sm border border-line bg-sand/50 p-12" data-testid="quote-success">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-moss/15">
                <Check className="h-6 w-6 text-moss" strokeWidth={1.5} />
              </span>
              <h2 className="mt-8 font-serif text-4xl tracking-tight text-forest">Richiesta ricevuta.</h2>
              <p className="mt-4 max-w-md leading-relaxed text-sage">
                Grazie {form.name.split(" ")[0]}. Ho preso in carico la tua richiesta per "{form.service}":
                ti scriverò presto all'indirizzo {form.email}.
              </p>
              <button
                onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", service: SERVICES[0], message: "" }); }}
                className="link-underline mt-8 font-accent text-xs uppercase tracking-[0.2em] text-terra"
                data-testid="quote-new-request"
              >
                Invia un'altra richiesta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-sm border border-line bg-sand/50 p-8 md:p-12" data-testid="quote-form">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Label htmlFor="q-name" className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Nome e cognome *</Label>
                  <Input id="q-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Maria Rossi" className={`mt-2 ${field}`} data-testid="quote-name-input" />
                </div>
                <div>
                  <Label htmlFor="q-email" className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Email *</Label>
                  <Input id="q-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="maria@email.it" className={`mt-2 ${field}`} data-testid="quote-email-input" />
                </div>
                <div>
                  <Label htmlFor="q-phone" className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Telefono</Label>
                  <Input id="q-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 333 000 0000" className={`mt-2 ${field}`} data-testid="quote-phone-input" />
                </div>
                <div>
                  <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Servizio richiesto *</Label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger className={`mt-2 ${field}`} data-testid="quote-service-select">
                      <SelectValue placeholder="Scegli un servizio" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`quote-service-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="q-message" className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Il tuo spazio *</Label>
                  <Textarea id="q-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Descrivi il giardino o terrazzo: dimensioni, esposizione, cosa vorresti realizzare…" className={`mt-2 ${field}`} data-testid="quote-message-input" />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-button"
                className="mt-8 flex items-center gap-3 rounded-full bg-forest px-10 py-4 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-105 active:scale-95 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Invia richiesta di preventivo
              </button>
            </form>
          )}
        </FadeUp>

        <FadeUp delay={0.2} className="lg:col-span-5">
          <div className="rounded-sm bg-forest p-8 text-bone md:p-12" data-testid="contact-info-panel">
            <h2 className="font-serif text-3xl tracking-tight">Parliamone di persona.</h2>
            <p className="mt-4 text-sm leading-relaxed text-bone/70">
              Preferisci una telefonata? Nessun problema. Il sopralluogo è sempre gratuito e senza impegno.
            </p>
            <div className="mt-10 flex flex-col gap-7">
              {INFO.map((i) => (
                <div key={i.label} className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-bone/20">
                    <i.icon className="h-4 w-4 text-terra" strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="font-accent text-[10px] uppercase tracking-[0.22em] text-bone/50">{i.label}</p>
                    <p className="mt-1 text-sm text-bone/90">{i.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>
      </section>
    </main>
  );
}
